import CalendarIcon from "assets/images/Calendar";
import ClockIcon from "assets/images/Clock";
import CloseIcon from "assets/images/CloseIcon";
import dropDownIcon from "assets/images/select-dropdown-icon.svg";
import classNames from "classnames";
import {format, parse} from "date-fns";
import {useState} from "react";
import {
  common as stringsCommon,
  confirmRefillRequest as strings,
} from "strings";

import Button from "components/Button";
import DatePickerModal from "components/DatePickerModal";
import Divider from "components/Divider";
import PageTitle from "components/PageTitle";
import StyledModal from "components/StyledModal";
import TextArea from "components/TextArea";
import TimePickerModal from "components/TimePickerModal";

import * as models from "liberty-core/api/models";
import {getAvailableTimes} from "liberty-core/sharedFunctions";

import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./ConfirmRefillRequest.module.css";

type Medication = {
  name: string;
  quantity?: number;
  isOnFile?: boolean;
};

function getFormattedTime(time: models.TimeString) {
  const parsedDate = parse(time, "HH:mm", new Date());

  return format(parsedDate, "p");
}

type ConfirmRefillRequestProps = {
  visible: boolean;
  onPickDate: (dateChosen: Date) => void;
  onPickTime: (dateChosen: models.TimeString) => void;
  onClickCancel: () => void;
  onClickRequest: () => void;
  onComment: (comment: string) => void;
  med: Medication[];
  datePicked?: Date | undefined;
  timePicked?: models.TimeString | undefined;
  comment: string;
  loading?: boolean;
};

export default function ConfirmRefillRequest(props: ConfirmRefillRequestProps) {
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const [isTimePickerVisible, setIsTimePickerVisible] = useState(false);

  const [pharmacy] = useCurrentPharmacy();

  return (
    <StyledModal className={styles.modal} visible={props.visible}>
      <div className={styles.container}>
        <div className={styles.detailsContainer}>
          <div className={styles.section}>
            <PageTitle className={styles.header}>{strings.title}</PageTitle>
            <button
              className={styles.close}
              onClick={() => {
                return props.onClickCancel();
              }}
            >
              <CloseIcon />
            </button>
          </div>
          <div className={styles.section}>
            {props.med.length === 1 ? (
              <div className={styles.medParentSingle}>
                <div className={styles.medNameSingle}>{props.med[0].name}</div>
                {props.med[0].isOnFile ? null : (
                  <div className={styles.medAmount}>
                    {strings.qty} {props.med[0].quantity}
                  </div>
                )}
              </div>
            ) : (
              <div>
                {props.med.map((med, index) => {
                  return (
                    <div key={index} className={styles.medParent}>
                      <div className={styles.medName}>{med.name}</div>
                      {med.quantity !== undefined ? (
                        <div className={styles.medAmount}>
                          {strings.qty} {med.quantity}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className={styles.chart}>
            <Divider
              className={
                props.med.length === 1
                  ? styles.topDividerSingle
                  : styles.topDivider
              }
            />

            <div className={styles.selectRow}>
              <div className={styles.selectRowLeft}>
                <div className={styles.webIcon}>
                  <CalendarIcon />
                </div>
                <div
                  className={classNames(styles.selectText, styles.selectLabel)}
                >
                  {strings.pickupDay}
                </div>
              </div>
              <button
                onClick={() => setIsDatePickerVisible(true)}
                className={styles.dropDown}
              >
                <div className={styles.selectText}>
                  {props.datePicked
                    ? props.datePicked.toLocaleDateString()
                    : strings.selectDate}
                </div>
                <img
                  src={dropDownIcon}
                  alt=""
                  className={styles.dropDownIcon}
                />
              </button>
              <DatePickerModal
                visible={isDatePickerVisible}
                onClickConfirm={(confirmedDate) => {
                  props.onPickDate(confirmedDate);
                  setIsDatePickerVisible(false);
                }}
                onClickCancel={() => setIsDatePickerVisible(false)}
                datePicked={props.datePicked}
                arePreviousDatesDisabled={true}
                enableCurrentDate={true}
                isDateAvailable={(dateToCheck) =>
                  !!getAvailableTimes(
                    dateToCheck,
                    pharmacy?.businessHours,
                    pharmacy?.leadTimeBuffer,
                    pharmacy?.closingTimeBuffer,
                  ).length
                }
              />
            </div>
            <Divider />
            <div className={styles.selectRow}>
              <div className={styles.selectRowLeft}>
                <div className={styles.webIcon}>
                  <ClockIcon color={props.datePicked ? undefined : "#9f9f9f"} />
                </div>
                <div
                  className={
                    props.datePicked
                      ? classNames(styles.selectText, styles.selectLabel)
                      : classNames(
                          styles.selectText,
                          styles.selectLabel,
                          styles.disabledText,
                        )
                  }
                >
                  {strings.pickupTime}
                </div>
              </div>
              <button
                onClick={() => setIsTimePickerVisible(true)}
                className={styles.dropDown}
                disabled={!props.datePicked}
              >
                <div
                  className={
                    props.datePicked
                      ? styles.selectText
                      : classNames(styles.selectText, styles.disabledText)
                  }
                >
                  {props.timePicked
                    ? getFormattedTime(props.timePicked)
                    : strings.selectTime}
                </div>
                <img
                  src={dropDownIcon}
                  alt=""
                  className={styles.dropDownIcon}
                />
              </button>
              <TimePickerModal
                allTimes={getAvailableTimes(
                  props.datePicked ?? new Date(),
                  pharmacy?.businessHours,
                  pharmacy?.leadTimeBuffer,
                  pharmacy?.closingTimeBuffer,
                )}
                availableTimes={
                  props.datePicked
                    ? getAvailableTimes(
                        props.datePicked,
                        pharmacy?.businessHours,
                        pharmacy?.leadTimeBuffer,
                        pharmacy?.closingTimeBuffer,
                      )
                    : []
                }
                onClickConfirm={(time: models.TimeString) => {
                  props.onPickTime(time);
                  setIsTimePickerVisible(false);
                }}
                onClickCancel={() => setIsTimePickerVisible(false)}
                visible={isTimePickerVisible}
              />
            </div>
            <Divider />
            <TextArea
              placeholder="Comments"
              value={props.comment}
              onChangeText={(text) => props.onComment(text)}
              className={styles.comment}
            />
          </div>
          <Button
            className={styles.requestButton}
            disabled={!props.datePicked || !props.timePicked}
            onClick={props.onClickRequest}
            loading={props.loading}
          >
            {strings.requestRefill}
          </Button>
          <Button variant="minimal" onClick={props.onClickCancel}>
            {stringsCommon.cancel}
          </Button>
        </div>
      </div>
    </StyledModal>
  );
}
